const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';

export class ChatService {
  static async handleStreamingResponse(response, callbacks) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullMessage = '';

    try {
      while (true) {
        if (callbacks.shouldStop?.()) {
          await reader.cancel();
          callbacks.onComplete(fullMessage);
          break;
        }

        const { done, value } = await reader.read();
        
        if (done) {
          callbacks.onComplete(fullMessage);
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          // Handle SSE format: data: {content}
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              // Handle error messages
              if (data.error) {
                callbacks.onError?.(data.error);
                return;
              }
              
              // Handle completion signal
              if (data.done) {
                callbacks.onComplete(fullMessage);
                return;
              }
              
              // Handle content
              if (data.content) {
                fullMessage += data.content;
                callbacks.onChunk(fullMessage);
                await new Promise(resolve => setTimeout(resolve, 30));
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
          // Handle legacy format: 0:{content} (for backward compatibility)
          else if (line.match(/^0:(.+)$/)) {
            const match = line.match(/^0:(.+)$/);
            try {
              const content = JSON.parse(match[1]);
              fullMessage += content;
              callbacks.onChunk(fullMessage);
              await new Promise(resolve => setTimeout(resolve, 30));
            } catch (e) {
              console.error('Error parsing streaming response:', e);
            }
          }
        }
      }
    } catch (e) {
      console.error('Error reading stream:', e);
      if (e.name === 'TypeError' && e.message.includes('body')) {
        callbacks.onError?.('Connection lost. Please check your internet connection.');
      } else {
        callbacks.onError?.('Error processing response stream');
      }
      throw new Error('Error processing response stream');
    }
  }

  static async handleError(response) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      console.error('Error parsing error response:', e);
      throw new Error('Network error - please try again');
    }
    if (response.status === 429) {
      return {
        isRateLimit: true,
        message: errorData.friendly_message || "You've reached the rate limit. Please wait before sending more messages.",
        retryAfter: errorData.retry_after
      };
    }
    throw new Error(errorData.detail || 'Failed to send message');
  }

  static async sendMessage(chatRequest) {
    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: chatRequest.message,
          session_id: chatRequest.sessionId,
          timestamp: Date.now() / 1000,
          messages: (chatRequest.messages || []).map(({ role, content }) => ({ role, content }))
        }),
      });

      if (!response.ok) {
        return this.handleError(response);
      }

      return response;
    } catch (error) {
      console.error('Network error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }
      throw error;
    }
  }
}